import type { Events } from '@setplex/pureya'

import { toAbs } from '~/features/rewind'
import { ONE_SECOND_MS, THIRTY_SEC_MS } from '~/shared/constants/time'

export function toSeconds(time: number) {
  return Math.round(time / ONE_SECOND_MS)
}

export function normalizeProgressBar(
  liveProgressBar: Events.Media.ProgressBarChanged
): { start: number; seekStart: number; seekEnd: number; end: number } | null {
  if (!liveProgressBar || liveProgressBar.length < 4) {
    return null
  }

  const start = toAbs(liveProgressBar[0])
  const end = toAbs(liveProgressBar[3])
  const seekStart = Math.max(toAbs(liveProgressBar[1]), start)
  const seekEnd = Math.min(toAbs(liveProgressBar[2]), end)

  return { start, seekStart, seekEnd, end }
}

export function normalizeTargetWithProgram({
  liveProgressBar,
  targetTimeSec,
}: {
  liveProgressBar: Events.Media.ProgressBarChanged
  targetTimeSec: number
}): number {
  targetTimeSec = Math.round(targetTimeSec)
  const bar = normalizeProgressBar(liveProgressBar)
  if (bar == null) return targetTimeSec

  const seekStartSec = toSeconds(bar.seekStart)
  const seekEndSec = toSeconds(bar.seekEnd)

  if (targetTimeSec < seekStartSec) return seekStartSec
  if (targetTimeSec > seekEndSec) return seekEndSec
  return targetTimeSec
}

export function isRewindableDurationLow(
  liveProgressBar: Events.Media.ProgressBarChanged
): boolean {
  const bar = normalizeProgressBar(liveProgressBar)
  if (bar == null) return false

  return bar.seekEnd - bar.seekStart < THIRTY_SEC_MS
}

/**
 * Get time to seek with player api, Dvr, already rewound
 */
export function getSeekTimePlayerApiDvr(
  liveProgressBar: Events.Media.ProgressBarChanged,
  targetTimeSec: number
): number {
  const bar = normalizeProgressBar(liveProgressBar)
  if (bar == null) return 0

  return targetTimeSec - toSeconds(bar.seekStart)
}

/**
 * Get time to seek with player api, Rewind, already rewound
 */
export function getSeekTimePlayerApiRewind(
  rewoundUrlStartSec: number,
  targetTimeSec: number
): number {
  return targetTimeSec - rewoundUrlStartSec
}

/**
 * Get startTime to fetch with rewind api, Dvr, no rewind yet
 */
export function getLowestStartTimeDvr(
  liveProgressBar: Events.Media.ProgressBarChanged,
  maxRewindLengthMs: number
): number {
  const bar = normalizeProgressBar(liveProgressBar)
  const absoluteMaxTime = Math.round(toSeconds(Date.now() - maxRewindLengthMs))
  return bar == null ? absoluteMaxTime : toSeconds(bar.seekStart)
}

/**
 * Get startTime to fetch with rewind api, Rewind, no rewind yet
 */
export function getLowestStartTimeRewind(
  _liveProgressBar: Events.Media.ProgressBarChanged,
  maxRewindLengthMs: number
): number {
  const absoluteMaxTime = Math.round(toSeconds(Date.now() - maxRewindLengthMs))
  return absoluteMaxTime
}

const CORRECTION_FOR_TIMESHIFT = 55

/**
 * Correct timeshift stamp
 */
export function getCorrectTimeshiftTime(
  liveProgressBar: Events.Media.ProgressBarChanged,
  targetTime: number
): number {
  const bar = normalizeProgressBar(liveProgressBar)

  if (bar == null) return targetTime

  const seekStart = bar.seekStart

  let correctedTime = targetTime

  if (Date.now() / 1000 - targetTime < CORRECTION_FOR_TIMESHIFT) {
    correctedTime = targetTime - CORRECTION_FOR_TIMESHIFT
  }

  correctedTime = Math.max(toSeconds(seekStart), correctedTime)

  return correctedTime
}
